import React from "react";

import { cn } from "../../../common/u1/cn";
import {
  ChevronDown,
  HomeIcon,
  DocumentIcon,
  CreditCardIcon,
  UploadIcon,
} from "../../../../assets/ui/icons";
import { EDIT_GROUPS, groupSections } from "./editSections";
import { ProfileCompletionCard } from "./ProfileCompletionCard";

/* The edit-profile rail: the completion card, then the four groups as an accordion, each opening onto its sections. */

/* Exported: the shell's floating shortcut shows the same mark, so the two
   cannot drift. */
export const GROUP_ICONS = {
  About: HomeIcon,
  Details: DocumentIcon,
  Financial: CreditCardIcon,
  "Upload Document": UploadIcon,
};

const Dot = ({ state }) => (
  <span
    aria-hidden="true"
    className={cn(
      "h-[7px] w-[7px] shrink-0 rounded-full",
      state === "active" && "bg-[var(--coral)]",
      state === "complete" && "bg-[var(--success)]",
      state === "todo" && "bg-[var(--line-2)]"
    )}
  />
);

export const EditSidebar = ({
  /* The live label of the section on screen. */
  activeSection,
  /* The group whose accordion is open — the live `activeAccordion`. It is
     tracked separately because collapsing a group leaves the section on
     screen, exactly as the live page does. */
  openGroup,
  onToggleGroup,
  onSelectSection,
  completed = [],
  completedCount = 0,
  totalCount = 0,
  className = "",
}) => {
  const isComplete = (label) => completed.includes(label);

  return (
    <div className={cn("flex w-full flex-col gap-4", className)}>
      <ProfileCompletionCard completed={completedCount} total={totalCount} />

      <nav aria-label="Profile sections">
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {EDIT_GROUPS.map((group) => {
            const Icon = GROUP_ICONS[group.id];
            const open = openGroup === group.id;
            const sections = groupSections(group.id);
            const panelId = `u1-edit-group-${group.step}`;

            return (
              <li key={group.id}>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => onToggleGroup?.(group.id)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2.5",
                    "text-[14px] font-bold text-[var(--ink)] font-sans",
                    "transition-colors hover:bg-[var(--ghost-hover)]",
                    "focus-visible:outline-none focus-visible:ring-2",
                    "focus-visible:ring-[var(--coral)]"
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg",
                      "border border-[var(--line)] bg-white text-[var(--coral)]"
                    )}
                  >
                    {Icon ? <Icon size={15} /> : null}
                  </span>

                  <span className="min-w-0 flex-1 truncate text-left">
                    {group.title}
                  </span>

                  <ChevronDown
                    size={12}
                    className={cn(
                      "shrink-0 text-[var(--muted-2)] transition-transform duration-200",
                      open && "rotate-180"
                    )}
                  />
                </button>

                {/* Unmounted when closed rather than hidden: the live rail
                    renders the body only while active, and a hidden list of
                    focusable buttons would still be reachable by keyboard. */}
                {open && (
                  <ul
                    id={panelId}
                    className="relative m-0 mb-1 list-none py-1 pl-[27px] pr-0"
                  >
                    {/* The connecting line down the dots. */}
                    <span
                      aria-hidden="true"
                      className="absolute bottom-3 left-[30px] top-3 w-px bg-[var(--line)]"
                    />

                    {sections.map((section) => {
                      const active = section.label === activeSection;
                      return (
                        <li key={section.label} className="relative">
                          <button
                            type="button"
                            aria-current={active ? "step" : undefined}
                            onClick={() => onSelectSection?.(section.label)}
                            className={cn(
                              "relative z-[1] flex w-full items-center gap-2.5 rounded-lg",
                              "px-2 py-[7px] text-left text-[13px] font-sans",
                              "transition-colors focus-visible:outline-none",
                              "focus-visible:ring-2 focus-visible:ring-[var(--coral)]",
                              active
                                ? "bg-[var(--coral-soft)] font-semibold text-[var(--coral-deep)]"
                                : "font-medium text-[var(--muted)] hover:bg-[var(--ghost-hover)]"
                            )}
                          >
                            <Dot
                              state={
                                active
                                  ? "active"
                                  : isComplete(section.label)
                                  ? "complete"
                                  : "todo"
                              }
                            />
                            <span className="min-w-0 flex-1 truncate">
                              {section.navLabel}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default EditSidebar;
